import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaEye, FaEyeSlash, FaClock, FaShieldAlt } from "react-icons/fa";

const LoginForm = ({ role, title = "Sign In", showRegisterLink = true }) => {
    const { login, error } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if redirected due to 30 min session expiry
    const isSessionExpired = new URLSearchParams(location.search).get("sessionExpired") === "true";

    // Form state
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [localError, setLocalError] = useState(null);

    const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return; // Prevent double click
        setLocalError(null);

        if (!form.email || !form.password) {
            setLocalError("Please enter your email and password.");
            return;
        }

        setSubmitting(true);

        const loginData = { ...form, role };
        const res = await login(loginData);
        setSubmitting(false);

        if (!res.ok) {
            if (res.code === "PENDING_APPROVAL") {
                navigate("/pending-approval", { replace: true });
            }
            return;
        }

        const userRole = res.data?.role;
        const isAdmin = userRole === "admin" || userRole === "superadmin";

        // If specific admin login requested, verify admin privileges
        if (role === "admin" || role === "superadmin") {
            if (!isAdmin) {
                setLocalError("Access denied. This account does not possess administrator credentials.");
                return;
            }
        }

        // Redirect based on role
        if (location.state?.from && location.state.from !== "/checkout") {
            navigate(location.state.from, { state: location.state, replace: true });
            return;
        }

        if (isAdmin) {
            navigate("/admin", { replace: true });
        } else {
            navigate("/customer", { replace: true });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[#F7F5EF] text-[#263746] font-sans relative overflow-hidden">
            <div className="w-full max-w-md bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-8 shadow-sm relative z-10">
                {/* Logo & Brand Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block mb-3 group">
                        <img
                            src="/logo.png"
                            alt="LUMIGO Projection Solutions"
                            className="h-12 mx-auto object-contain group-hover:scale-105 transition-transform"
                        />
                    </Link>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E3B629]/10 border border-[#E3B629]/30 text-[#263746] text-[10px] font-black tracking-widest uppercase mb-1">
                        <img src="/favicon.png" alt="LUMIGO Aperture" className="w-3.5 h-3.5 object-contain" />
                        <span>Commercial Projection Systems</span>
                    </div>
                    <h1 className="text-2xl font-extrabold text-[#263746]">
                        {role === "admin" || role === "superadmin" ? "Administrator Portal" : "Commercial Client Sign In"}
                    </h1>
                    <p className="text-[#687482] mt-1.5 text-xs">
                        {role === "admin" || role === "superadmin"
                            ? "Sign in to manage commercial enquiries, quotations, and equipment."
                            : "Access your project enquiries, quotation history, and order statuses."}
                    </p>
                </div>

                {/* Session Expiry Banner (30 Min Expiry) */}
                {isSessionExpired && (
                    <div className="mb-6 text-xs bg-amber-50 border border-amber-200 text-amber-800 p-3.5 rounded-xl flex items-center gap-2.5 font-medium shadow-xs">
                        <FaClock className="text-amber-600 text-base flex-shrink-0" />
                        <span>Your session has expired after <strong>30 minutes</strong> for your security. Please sign in again.</span>
                    </div>
                )}

                {(localError || error) && (
                    <div className="mb-6 text-xs bg-rose-50 border border-rose-200 text-rose-700 p-3 rounded-xl text-center font-bold">
                        {localError || error}
                    </div>
                )}

                <form onSubmit={onSubmit} className="space-y-4">
                    <div>
                        <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            required
                            name="email"
                            value={form.email}
                            onChange={onChange}
                            placeholder={role === "admin" || role === "superadmin" ? "skhandagle1233@gmail.com" : "name@company.com"}
                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] font-bold text-[#263746] uppercase tracking-wider block mb-1">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                name="password"
                                value={form.password}
                                onChange={onChange}
                                placeholder="••••••••••••"
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629] transition pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687482] hover:text-[#263746] transition p-1 text-sm"
                                title={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-[#687482] pt-1">
                        <span className="flex items-center gap-1">
                            <FaShieldAlt className="text-emerald-600 text-xs" />
                            30-min secure session timeout
                        </span>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold py-3 rounded-lg text-xs uppercase tracking-wider transition shadow-sm disabled:opacity-50 mt-2 flex items-center justify-center gap-2 cursor-pointer"
                    >
                        {submitting ? (
                            <>
                                <span className="w-4 h-4 border-2 border-[#263746] border-t-transparent rounded-full animate-spin"></span>
                                <span>Authenticating...</span>
                            </>
                        ) : (
                            "Sign In"
                        )}
                    </button>
                </form>

                {showRegisterLink && (
                    <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center text-xs text-[#687482]">
                        Don't have a commercial account?{" "}
                        <Link to="/register" className="text-[#E3B629] hover:underline font-bold">
                            Register Company
                        </Link>
                    </div>
                )}

                <div className="mt-4 text-center">
                    <Link to="/" className="text-[11px] text-[#687482] hover:text-[#263746] transition font-medium">
                        &larr; Return to Public Website
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LoginForm;
